// src/pages/ApplicationNews/components/NewsTable/NewsTable.tsx
import { Box, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, Image, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { colorByNewStatus, News, NewsStatus, translateNewStatus } from '@/model';
import { useTranslation } from 'react-i18next';

export interface NewsTableProps {
  data: News[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: News) => void;
  onDeleteClick: (item: News) => void;
}

export const NewsTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: NewsTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<News>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_news.components.news_table.news_table_created_at_header'),
    }),
    columnHelper.display({
      id: 'publish_cell',
      header: t('pages.application_news.components.news_table.news_table_publish_date_header'),
      cell: props => (
        <Text>
          {props.row.original.status === NewsStatus.PUBLISHED && props.row.original.published_at
            ? formatDateToDateTimeDisplay(props.row.original.published_at)
            : props.row.original.publish_at
              ? formatDateToDateTimeDisplay(props.row.original.publish_at)
              : '-'}
        </Text>
      ),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_news.components.news_table.news_table_team_name_header'),
    }),

    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: t('pages.application_news.components.news_table.news_table_title_header'),
    }),

    columnHelper.accessor('status', {
      cell: props => (
        <Text position='relative' paddingLeft='16px'>
          <Box
            as='span'
            position='absolute'
            top='50%'
            left='0'
            transform='translateY(-50%)'
            w='8px'
            h='8px'
            borderRadius='50%'
            bgColor={colorByNewStatus(props.getValue())}
            zIndex={9999}
            aria-hidden='true'
          />
          {translateNewStatus(props.getValue())}
        </Text>
      ),
      header: t('pages.application_news.components.news_table.news_table_status_header'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_news.components.news_table.news_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_news.components.news_table.news_table_menu_aria_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as News)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_news.components.news_table.news_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as News)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_news.components.news_table.news_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
